import type { ReactNode } from "react";
import { useCallback, useState } from "react";
import type {
  AutocompleteRenderInputParams,
  AutocompleteInputChangeReason,
  AutocompleteProps,
} from "@mui/material";
import { Autocomplete, Skeleton, TextField, Typography } from "@mui/material";
import { multiAutoClassNames, multiAutoUtil } from "utils";

function defaultGetOptionKey<T>(t: T): string {
  const u = t as { id: string };
  return u.id;
}

function defaultGetOptionLabel<T>(t: T): string {
  const u = t as { libelle: string };
  return u.libelle;
}

function defaultIsOptionEqualToValue<T>(o: T, v: T): boolean {
  const o2 = o as { id: string };
  const v2 = v as { id: string };
  return o2.id === v2.id;
}

interface PartialAutoWithOptions<T> extends Omit<
  Partial<AutocompleteProps<T, true, false, false>>,
  "onChange"
> {
  options: Array<T>;
}

interface MultiAutoCompleteProps<T> extends PartialAutoWithOptions<T> {
  singleItemLabel: string;
  value: Array<T>;
  onChange?: (newValue: Array<T>) => void;
  label?: string;
  placeholder?: string;
  size?: "small" | "medium";
  isLoading: boolean;
}

export function MultiAutoComplete<T>({
  singleItemLabel,
  value,
  onChange,
  label,
  placeholder,
  size = "medium",
  isLoading = false,
  ...overrideProps
}: Readonly<MultiAutoCompleteProps<T>>): ReactNode {
  const [input, setInput] = useState<string>("");
  const [isOpen, setIsOpen] = useState(false);

  const renderValue = useCallback(
    (ts: Array<T>): ReactNode =>
      ts.length > 0 &&
      input === "" &&
      !isOpen && (
        <Typography className="selectedLabel" noWrap>
          <strong>{ts.length}</strong> {multiAutoUtil.getSelectedLabel(singleItemLabel, ts.length)}
        </Typography>
      ),
    [input, isOpen, singleItemLabel]
  );

  const renderInput = useCallback(
    (params: AutocompleteRenderInputParams): ReactNode => (
      <TextField
        {...params}
        label={label}
        placeholder={
          value.length === 0 || !isOpen
            ? placeholder
            : `${value.length} ${multiAutoUtil.getSelectedLabel(singleItemLabel, value.length)}`
        }
      />
    ),
    [isOpen, label, placeholder, singleItemLabel, value.length]
  );

  return (
    <Autocomplete
      size={size}
      value={value}
      onChange={(_e, newValue: Array<T>) => {
        onChange?.(newValue);
      }}
      inputValue={input}
      onInputChange={(_, inputValue: string, reason: AutocompleteInputChangeReason) => {
        if (!["selectOption", "reset"].includes(reason)) {
          setInput(inputValue);
        }
      }}
      open={isOpen}
      onOpen={(_) => {
        setIsOpen(true);
      }}
      onClose={(_) => {
        setIsOpen(false);
      }}
      multiple
      disableCloseOnSelect
      fullWidth
      autoHighlight
      openOnFocus
      selectOnFocus
      clearOnBlur
      handleHomeEndKeys
      className={multiAutoClassNames.SDEAPPS_MULTI_AUTOCOMPLETE}
      renderValue={renderValue}
      renderInput={renderInput}
      getOptionKey={defaultGetOptionKey<T>}
      getOptionLabel={defaultGetOptionLabel<T>}
      isOptionEqualToValue={defaultIsOptionEqualToValue<T>}
      noOptionsText={isLoading ? <Skeleton /> : "Pas de résultat"}
      {...overrideProps}
      sx={{
        [`&.${multiAutoClassNames.SDEAPPS_MULTI_AUTOCOMPLETE} .selectedLabel`]: {
          pl: 1,
          strong: { fontWeight: 600, color: "info.dark" },
        },
        ...overrideProps?.sx,
      }}
      slotProps={{
        ...overrideProps.slotProps,
        popper: {
          sx: { ...multiAutoUtil.multiSelectWithCheckboxesSxProps },
          ...overrideProps.slotProps?.popper,
        },
      }}
    />
  );
}
